/**
 * Cálculo de pesos con GRUPOS DE VARIANTE.
 *
 * Un mismo aspecto puede tener varias redacciones según el enfoque del programa
 * (T y T, universitario, maestría…). Esas variantes son hermanos en el árbol pero
 * representan UN SOLO aspecto lógico, así que deben ocupar un único slot de peso:
 * contarlas por separado inflaría la suma y descuadraría el 100 % del padre.
 *
 * Los hermanos que comparten `grupo_variante` se colapsan; los que no lo tienen
 * cuentan de uno en uno.
 */

import type Componente from '@/interfaces/Componente'

/**
 * Suma de pesos contando cada `grupo_variante` UNA sola vez.
 *
 * Toma el peso del PRIMER nodo de cada grupo en el orden del array; se asume que
 * las variantes del mismo grupo comparten peso, que es la invariante que impone
 * el backend. Si difirieran, el resultado dependería del orden.
 *
 * Complejidad: O(n), con un `Set` de grupos ya vistos.
 *
 * @param nodos - Hermanos del mismo nivel.
 * @returns Suma colapsada. Un `peso` nulo cuenta como 0.
 */
export function sumaColapsada(nodos: Componente[]): number {
  let suma = 0
  const grupos = new Set<string>()
  for (const n of nodos) {
    const g = n.grupo_variante
    if (!g) { suma += n.peso ?? 0 }
    else if (!grupos.has(g)) { grupos.add(g); suma += n.peso ?? 0 }
  }
  return suma
}

/**
 * Número de slots de peso entre hermanos: los nodos sin grupo cuentan 1 cada
 * uno, y cada `grupo_variante` cuenta 1 en total.
 *
 * Es el divisor de la autodistribución: repartir el 100 % entre el número de
 * hermanos, en lugar de entre el número de slots, daría a cada variante su propia
 * porción y el conjunto sumaría de más.
 *
 * Complejidad: O(n).
 *
 * @param nodos - Hermanos del mismo nivel.
 * @returns Número de slots. 0 si el array está vacío — el llamador debe evitar
 *          dividir por este valor sin comprobarlo.
 */
export function contarSlots(nodos: Componente[]): number {
  let slots = 0
  const grupos = new Set<string>()
  for (const n of nodos) {
    const g = n.grupo_variante
    if (!g) slots++
    else if (!grupos.has(g)) { grupos.add(g); slots++ }
  }
  return slots
}

/**
 * Color determinístico para un grupo de peso: el mismo nombre de grupo siempre
 * produce el mismo color, así se distinguen visualmente en la vista de pesos.
 */
export function colorDeGrupo(grupo: string): { bg: string; borde: string; texto: string } {
  let h = 0
  for (let i = 0; i < grupo.length; i++) {
    h = (h * 31 + grupo.charCodeAt(i)) % 360
  }
  return {
    bg: `hsl(${h}, 72%, 93%)`,
    borde: `hsl(${h}, 58%, 52%)`,
    texto: `hsl(${h}, 65%, 30%)`,
  }
}
